import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography } from '@mui/material';                  // your axios instance
import ReleaseLogItem from '../components/ReleaseLogItem';
import Navbar from '../components/Navbar';
import ReleaseHighlightBanner from '../components/ReleaseHighlightBanner';
import api from '../api';
import ShimmerLoader from '../components/ShimmerLoader';

const TabHeader = ({ title, subtitle }) => (
    <Box mb={4}>
        <Typography
            variant="h5"
            sx={{ fontWeight: 600, mb: 1, color: 'black', fontSize: '24px' }}
        >
            {title}
        </Typography>
        <Typography
            variant="body1"
            sx={{ color: '#7c7c83', fontWeight: 500, fontSize: '16px' }}
        >
            {subtitle}
        </Typography>
    </Box>
);

export default function ReleaseLogs() {
    const [allLogs, setAllLogs] = useState([]);
    const [latest, setLatest] = useState(null);
    const [expandedIdx, setExpandedIdx] = useState(-1);
    const [loadingLatest, setLoadingLatest] = useState(true);
    const [loadingAll, setLoadingAll] = useState(true);

    // grab your JWT once
    const [token] = useState(() => {
        const stored = localStorage.getItem('token');
        return stored ? JSON.parse(stored).token : '';
    });

    // fetch the **latest** release
    const fetchLatest = useCallback(async () => {
        setLoadingLatest(true);
        try {
            const res = await api.get('/v1/release-logs', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLatest(res.data.data || null);
        } catch (err) {
            console.error('Error fetching latest release', err);
        } finally {
            setLoadingLatest(false);
        }
    }, [token]);

    // fetch **all** releases
    const fetchAll = useCallback(async () => {
        setLoadingAll(true);
        try {
            const res = await api.get('/v1/release-logs/all', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAllLogs(res.data.data || []);
        } catch (err) {
            console.error('Error fetching all releases', err);
        } finally {
            setLoadingAll(false);
        }
    }, [token]);

    // run both on mount
    useEffect(() => {
        fetchLatest();
        fetchAll();
    }, [fetchLatest, fetchAll]);

    return (
        <>
            {/* Navbar pill + popover */}
            <Navbar
                title="Release Logs"
            />

            {/* Highlight banner for the latest */}
            {loadingLatest ? (
                <Box sx={{ p: 4, mb: 2 }}>
                    <ShimmerLoader height="160px" width="100%" borderRadius="8px" />
                </Box>
            ) : latest && (
                <ReleaseHighlightBanner
                    latestRelease={latest[0]}
                    onViewFull={() => setExpandedIdx(0)}
                />
            )}

            {/* Full log listing */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, p: 4 }}>
                <Box
                    id="full-release-log"
                    flex={1}
                    sx={{
                        p: 4,
                        bgcolor: '#fff',
                        borderColor: '#E5E7EB',
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        borderRadius: 2,
                        minHeight: '80vh'
                    }}
                >
                    <TabHeader
                        title="Release Logs"
                        subtitle="Stay updated with the latest features and improvements."
                    />

                    {loadingAll ? (
                        <>
                            <ShimmerLoader height="80px" width="100%" mb={2} />
                            <ShimmerLoader height="80px" width="100%" mb={2} />
                            <ShimmerLoader height="80px" width="100%" mb={2} />
                        </>
                    ) : (
                        allLogs.map((r, idx) => (
                            <ReleaseLogItem
                                key={r.version}
                                version={r.version}
                                date={r.date}
                                title={r.title}
                                totalChanges={r.total_client_updates}
                                changes={r.client_updates}
                                expanded={idx === expandedIdx}
                                onToggle={isOpen =>
                                    setExpandedIdx(isOpen ? idx : -1)
                                }
                            />
                        ))
                    )}
                </Box>
            </Box>
        </>
    );
}